import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { ArrowLeft, FilePlus2, StickyNote, CalendarPlus, UserPlus, Package, MapPin, CalendarDays, Wallet, Flag, Search, HardHat } from "lucide-react";
import { toast } from "sonner";
import { euro } from "@/data/partnerConfig";
import { useProjects } from "@/engine/projects";
import { Card, Btn, Pill, InfoRow, SlideOver, Field, inputCls, Segmented } from "../ui";
import { PhaseStepper } from "./PartnerProjects";
import { useEngine } from "@/engine/store";
import { RoleBar } from "@/engine/shared";
import { usePartner, APPOINTMENT_TYPE_LABELS } from "../PartnerContext";
import { AppointmentModal } from "../components/AppointmentModal";
import { OverviewEngine, LotsTab, PlanningTab, ChantierTab, FinancesTab, DocumentsEngineTab, EchangesTab } from "./ProjectEngineTabs";

const TABS = ["Vue d'ensemble", "Projet", "Documents", "Professionnels & devis", "Planning", "Chantier", "Finances", "Échanges"];

const ACTIONS = [
  { label: "Ajouter un document", icon: FilePlus2 },
  { label: "Ajouter une note", icon: StickyNote, action: "note" },
  { label: "Ajouter un rendez-vous", icon: CalendarPlus, action: "appointment" },
  { label: "Ajouter un professionnel", icon: UserPlus, action: "professional" },
  { label: "Commander un service CozyHome", icon: Package, to: "/partenaire/services" },
];

const AddNoteModal = ({ open, onClose, project }) => {
  const engine = useEngine();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) { toast.error("Le contenu de la note est requis."); return; }
    setBusy(true);
    try {
      await engine.addProjectNote(project.ref, title.trim(), text.trim());
      toast.success("Note interne ajoutée à l'historique du projet.");
      setTitle(""); setText(""); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'ajout de la note.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <SlideOver open={open} onClose={onClose} testid="add-note-modal" title="Ajouter une note interne" eyebrow="Strictement interne — jamais visible du client">
      <div className="space-y-3 p-5">
        <Field label="Titre (facultatif)">
          <input data-testid="note-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Point téléphonique avec le client" />
        </Field>
        <Field label="Note / commentaire">
          <textarea data-testid="note-text" rows={5} className={inputCls} value={text} onChange={(e) => setText(e.target.value)} placeholder="Contenu de la note..." />
        </Field>
        <p className="text-xs text-stone-400">Date, heure et auteur sont ajoutés automatiquement. Visible uniquement dans l'Historique de l'Espace Partenaire.</p>
        <Btn testid="note-submit" disabled={busy} onClick={submit}>Enregistrer la note</Btn>
      </div>
    </SlideOver>
  );
};

const AddProfessionalModal = ({ open, onClose, project }) => {
  const engine = useEngine();
  const [mode, setMode] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ name: "", trade: "", email: "", phone: "", siret: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || mode !== "search") return;
    const t = setTimeout(() => {
      engine.companiesDirectory(q).then(setResults).catch(() => toast.error("Recherche impossible."));
    }, 300);
    return () => clearTimeout(t);
  }, [open, mode, q, engine]);

  const attach = async (companyId, origin) => {
    setBusyId(companyId);
    try {
      await engine.attachCompanyToProject(project.ref, companyId, origin);
      toast.success("Professionnel ajouté au projet.");
      onClose();
    } finally {
      setBusyId(null);
    }
  };

  const submitCreate = async () => {
    if (!form.name.trim() || !form.trade.trim()) { toast.error("Raison sociale et métier sont requis."); return; }
    setCreating(true);
    try {
      const company = await engine.createCompany(form.name.trim(), form.trade.trim(), form.email.trim(), form.phone.trim(), form.siret.trim());
      await engine.attachCompanyToProject(project.ref, company.id, "partner");
      toast.success("Professionnel référencé et ajouté au projet.");
      setForm({ name: "", trade: "", email: "", phone: "", siret: "" });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'ajout du professionnel.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <SlideOver open={open} onClose={onClose} testid="add-professional-modal" title="Ajouter un professionnel" eyebrow="Rattaché au projet — aucun devis ni compte créé automatiquement">
      <div className="space-y-4 p-5">
        <Segmented
          testid="professional-mode"
          value={mode}
          onChange={setMode}
          options={[{ value: "search", label: "Rechercher une entreprise" }, { value: "create", label: "Ajouter mon entreprise / artisan" }]}
        />
        {mode === "search" ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <input data-testid="professional-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom ou métier…" className={`${inputCls} pl-9`} />
            </div>
            {results.length === 0 ? (
              <p className="text-sm text-stone-400" data-testid="professional-search-empty">Aucune entreprise trouvée.</p>
            ) : (
              <ul className="divide-y divide-[#F0F2F3]">
                {results.map((c) => (
                  <li key={c.id} data-testid={`professional-result-${c.id}`} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                    <span><b className="text-navy">{c.name}</b> <span className="text-stone-500">— {c.trade}</span></span>
                    <Btn className="px-3 py-1.5 text-xs" testid={`professional-result-${c.id}-add`} disabled={busyId === c.id} onClick={() => attach(c.id, "network")}>Ajouter au projet</Btn>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Raison sociale / nom *">
              <input data-testid="professional-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Métier *">
              <input data-testid="professional-trade" className={inputCls} value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="Ex. Électricité, Plomberie…" />
            </Field>
            <Field label="Email">
              <input data-testid="professional-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Téléphone">
              <input data-testid="professional-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="SIRET (facultatif)">
              <input data-testid="professional-siret" className={inputCls} value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
            </Field>
            <p className="text-xs text-stone-400">Référencer une entreprise ne crée pas de compte Espace Entreprise. C'est une action distincte.</p>
            <Btn testid="professional-submit" disabled={creating} onClick={submitCreate}>
              <HardHat className="h-4 w-4" /> Ajouter au projet
            </Btn>
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default function PartnerProjectDetail() {
  const { ref } = useParams();
  const engine = useEngine();
  const ep = engine.project(ref);
  const p = useProjects().find((x) => x.ref === ref);
  const [tab, setTab] = useState(TABS[0]);
  const { territorialProspects, appointments } = usePartner();
  const [apptOpen, setApptOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  if (!p || !ep) return <Navigate to="/partenaire/projets" replace />;

  const prospectForClient = (territorialProspects || []).find((tp) => tp.clientProfileId === ep.client?.id);
  const projectAppointments = (appointments || []).filter((a) => a.project_ref === ep.ref).sort((a, b) => a.start_at.localeCompare(b.start_at));
  const projectCompanies = Object.values(ep.companies || {});
  const openAppointment = () => {
    if (!prospectForClient) { toast.error("Aucun prospect propriétaire trouvé pour ce client — impossible de créer le rendez-vous."); return; }
    setApptOpen(true);
  };
  const ACTION_HANDLERS = { appointment: openAppointment, note: () => setNoteOpen(true), professional: () => setProOpen(true) };

  return (
    <div data-testid="partner-project-detail">
      <Link to="/partenaire/projets" data-testid="project-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-petrol">
        <ArrowLeft className="h-4 w-4" /> Tous les projets
      </Link>

      <Card className="mt-4 overflow-hidden">
        <div className="h-1 bg-petrol" />
        <div className="p-5 sm:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid="project-ref" className="rounded-md bg-petrol-tint px-2 py-1 font-mono text-xs font-bold text-petrol">{p.ref}</span>
                <Pill tone="petrol">{p.phase}</Pill>
              </div>
              <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">{p.name}</h1>
              <p className="mt-1 text-sm font-semibold text-stone-600">{p.client}</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-stone-500"><MapPin className="h-3.5 w-3.5" /> {p.address}</p>
              <div className="mt-4 max-w-md"><PhaseStepper phase={p.phase} /></div>
            </div>
            <dl className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
              {[
                ["Budget travaux", euro(p.budget), Wallet],
                ["Prochain RDV", p.nextMeeting, CalendarDays],
                ["Prochaine étape", p.nextStep, Flag],
              ].map(([l, v, Icon]) => (
                <div key={l} className="rounded-xl bg-petrol-bg p-3 last:col-span-2 sm:last:col-span-2">
                  <dt className="flex items-center gap-1 text-[11px] font-bold text-stone-500"><Icon className="h-3 w-3 text-petrol" /> {l}</dt>
                  <dd className="mt-1 text-sm font-semibold text-navy">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="project-quick-actions">
            {ACTIONS.map((a) =>
              a.to ? (
                <Btn key={a.label} to={a.to} variant="copper" testid="project-action-service"><a.icon className="h-4 w-4" /> {a.label}</Btn>
              ) : (
                <Btn
                  key={a.label} variant="ghost" testid={`project-action-${a.label.split(" ")[2]}`}
                  onClick={ACTION_HANDLERS[a.action] || (() => toast.info(`« ${a.label} » sera connecté dans la phase suivante.`))}
                >
                  <a.icon className="h-4 w-4" /> {a.label}
                </Btn>
              )
            )}
          </div>
        </div>
      </Card>

      <div className="mt-6 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex min-w-max gap-1 border-b border-[#E3E8E9]" role="tablist" data-testid="project-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              data-testid={`project-tab-${t.split(" ")[0].toLowerCase().replace("'", "")}`}
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors ${tab === t ? "border-petrol text-petrol" : "border-transparent text-stone-500 hover:text-navy"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5" data-testid="project-tab-content">
        <RoleBar current="partenaire" ref={ref} />
        {tab === "Vue d'ensemble" && (
          <>
            <OverviewEngine project={ep} setTab={setTab} />
            <Card className="mt-4 p-5" testid="project-appointments">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="font-display text-lg font-medium text-navy">Rendez-vous du projet</p>
                <Pill tone="petrol">{projectAppointments.length}</Pill>
              </div>
              {projectAppointments.length === 0 ? (
                <p className="text-sm text-stone-400">Aucun rendez-vous rattaché à ce projet pour le moment.</p>
              ) : (
                <ul className="divide-y divide-[#F0F2F3] text-sm">
                  {projectAppointments.map((a) => (
                    <li key={a.id} data-testid={`project-appointment-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                      <span className="font-semibold text-navy">{dayjs(a.start_at).format("DD/MM/YYYY")} à {dayjs(a.start_at).format("HH:mm")}</span>
                      <span className="text-stone-500">{APPOINTMENT_TYPE_LABELS[a.appointment_type]}</span>
                      <Pill tone={a.status === "ANNULE" ? "stone" : a.status === "REALISE" ? "sage" : "petrol"}>{a.status === "PLANIFIE" ? "Prévu" : a.status === "REALISE" ? "Réalisé" : "Annulé"}</Pill>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
        {tab === "Documents" && <DocumentsEngineTab project={ep} />}
        {tab === "Professionnels & devis" && (
          <>
            <Card className="mb-4 p-5" testid="project-professionals">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="font-display text-lg font-medium text-navy">Professionnels du projet</p>
                <Pill tone="petrol">{projectCompanies.length}</Pill>
              </div>
              {projectCompanies.length === 0 ? (
                <p className="text-sm text-stone-400">Aucun professionnel rattaché à ce projet pour le moment.</p>
              ) : (
                <ul className="divide-y divide-[#F0F2F3] text-sm">
                  {projectCompanies.map((c) => (
                    <li key={c.id} data-testid={`project-professional-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                      <span><b className="text-navy">{c.name}</b> <span className="text-stone-500">— {c.trade}</span></span>
                      <span className="flex items-center gap-2">
                        <Pill tone={c.origin === "partner" ? "copper" : "petrol"}>{c.origin === "partner" ? "Apporté par vous" : "Réseau CozyHome"}</Pill>
                        <Pill tone={c.commissionExcluded ? "stone" : "sage"}>{c.commissionExcluded ? "Hors commission" : "Commission 5 % CozyHome"}</Pill>
                        <button
                          data-testid={`project-professional-${c.id}-commission-toggle`}
                          className="text-xs font-semibold text-petrol underline"
                          onClick={() => engine.setCompanyCommission(ep.ref, c.id, !c.commissionExcluded)}
                        >
                          {c.commissionExcluded ? "Réactiver la commission" : "Passer hors commission"}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <LotsTab project={ep} />
          </>
        )}
        {tab === "Planning" && <PlanningTab project={ep} />}
        {tab === "Chantier" && <ChantierTab project={ep} />}
        {tab === "Finances" && <FinancesTab project={ep} />}
        {tab === "Échanges" && <EchangesTab project={ep} />}
        {tab === "Projet" && (
          <Card className="p-5">
            <InfoRow label="Référence" value={p.ref} />
            <InfoRow label="Client" value={p.client} />
            <InfoRow label="Adresse" value={p.address} />
            <InfoRow label="Phase" value={p.phase} />
            <InfoRow label="Budget travaux" value={euro(p.budget)} />
            <p className="mt-3 text-sm leading-relaxed text-stone-600" data-testid="project-program">{ep.program}</p>
          </Card>
        )}
      </div>
      <AppointmentModal open={apptOpen} onClose={() => setApptOpen(false)} prospectId={prospectForClient?.id} projectRef={ep.ref} />
      <AddNoteModal open={noteOpen} onClose={() => setNoteOpen(false)} project={ep} />
      <AddProfessionalModal open={proOpen} onClose={() => setProOpen(false)} project={ep} />
    </div>
  );
}
